/**
 * /setup - choose the dam and failure case, set the run's parameters. Section 12.1.
 *
 * The map frames the study area on the shared satellite basemap: the dam, the
 * Bhima reach, the downstream settlements and the state boundaries. Every symbol is centred on its coordinate - labels sit
 * beside the symbol, never under it, so the mark is where the thing is.
 */

import { useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Layer, type MapRef, Marker, Source } from 'react-map-gl/maplibre'
import type { LayerSpecification, SourceSpecification } from 'maplibre-gl'

import { api, type GeoJsonFeatureCollection } from '../api/client'
import type { Dam, Settlement } from '../api/types'
import { ScenarioBuilder, type SitesPayload } from '../components/ScenarioBuilder'
import { CollapsibleLegend } from '../components/MapCorner'
import { BaseMap } from '../components/BaseMap'
import { geoUrl, useGeo, type GeoCollection } from '../components/geo'
import { useAppStore } from '../store/useAppStore'
import { C } from '../design/tokens'

type Bbox = [number, number, number, number]
type DamFull = Dam & { dam_crest_level_m?: number | null }

const LABEL = 'text-12 font-medium uppercase tracking-[0.06em] text-[var(--ink-text-dim)]'
const RIVER = C.mapRiver
const DAM = C.alert
const PAD = 16

// Natural Earth state boundaries (bundled, frontend/public/geo), over the imagery.
const DATA_SOURCES: Record<string, SourceSpecification> = {
  states: { type: 'geojson', data: geoUrl('india_states.geojson') },
}
const DATA_LAYERS: LayerSpecification[] = [
  {
    id: 'state-lines',
    type: 'line',
    source: 'states',
    layout: { 'line-join': 'round' },
    paint: { 'line-color': C.mapBoundary, 'line-width': 1.25, 'line-dasharray': [4, 2] },
  },
]

const MAP_CSS = `
/* The attribution is right-aligned but lays out across the whole bottom row; capped
   and lifted clear it no longer covers the scale bar's distance. */
.setup-map .maplibregl-ctrl-bottom-left{z-index:3}
.setup-map .maplibregl-ctrl-bottom-right{max-width:58%}
.setup-map .maplibregl-ctrl-scale{background:rgb(14 17 22/88%);border:1px solid var(--ink-text-dim);border-top:none;color:var(--ink-text);font:var(--fs-12)/18px var(--font-mono);padding:0 6px;white-space:nowrap}
.setup-map .maplibregl-ctrl-group{background:var(--ink-surface);border:1px solid var(--ink-border);border-radius:4px;box-shadow:none}
.setup-map .maplibregl-ctrl-group button+button{border-top:1px solid var(--ink-border)}
.setup-map .maplibregl-ctrl-group button .maplibregl-ctrl-icon{filter:invert(.88)}
.setup-map .maplibregl-ctrl-attrib{background:rgb(14 17 22/88%);color:var(--ink-text-dim);font-size:var(--fs-12)}
.setup-map .maplibregl-ctrl-attrib a{color:var(--ink-text-dim)}
`

type Geometry = GeoCollection['features'][number]['geometry']

function inRing(x: number, y: number, ring: number[][]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

function polygons(g: Geometry): number[][][][] {
  if (g.type === 'Polygon') return [g.coordinates as number[][][]]
  if (g.type === 'MultiPolygon') return g.coordinates as number[][][][]
  return []
}

function geomBbox(g: Geometry): Bbox {
  let w = Infinity
  let s = Infinity
  let e = -Infinity
  let n = -Infinity
  for (const poly of polygons(g))
    for (const [x, y] of poly[0]) {
      w = Math.min(w, x)
      e = Math.max(e, x)
      s = Math.min(s, y)
      n = Math.max(n, y)
    }
  return [w, s, e, n]
}

/**
 * Where to write each state's name: a sample grid over the view, point-in-polygon
 * tested - so a state gets a label only where it is actually visible. Of its own
 * samples the label takes the one furthest from the view's edge and from every
 * symbol on the map, which is what keeps the name off the marks.
 */
function stateLabels(states: GeoCollection | undefined, b: Bbox, avoid: { lon: number; lat: number }[]) {
  if (!states) return []
  const feats = states.features
    .map((f) => ({ name: f.properties.name, polys: polygons(f.geometry), box: geomBbox(f.geometry) }))
    .filter((f) => f.box[0] <= b[2] && f.box[2] >= b[0] && f.box[1] <= b[3] && f.box[3] >= b[1])
  const N = 24
  const w = b[2] - b[0]
  const h = b[3] - b[1]
  // Normalised to the view, so the two axes weigh the same whatever the aspect.
  const clearance = (x: number, y: number) => {
    const u = (x - b[0]) / w
    const v = (y - b[1]) / h
    let d = Math.min(u, 1 - u, v, 1 - v)
    for (const a of avoid) {
      const du = u - (a.lon - b[0]) / w
      const dv = v - (a.lat - b[1]) / h
      d = Math.min(d, Math.hypot(du, dv))
    }
    return d
  }
  const best: Record<string, { lon: number; lat: number; score: number; n: number }> = {}
  for (let i = 0; i < N; i++)
    for (let j = 0; j < N; j++) {
      const x = b[0] + ((i + 0.5) / N) * w
      const y = b[1] + ((j + 0.5) / N) * h
      const hit = feats.find(
        (f) => f.polys.some((p) => inRing(x, y, p[0]) && !p.slice(1).some((h2) => inRing(x, y, h2))),
      )
      if (!hit) continue
      const score = clearance(x, y)
      const cur = (best[hit.name] ??= { lon: x, lat: y, score: -1, n: 0 })
      cur.n += 1
      if (score > cur.score) {
        cur.lon = x
        cur.lat = y
        cur.score = score
      }
    }
  return Object.entries(best)
    .filter(([, a]) => a.n >= N * N * 0.03)
    .map(([name, a]) => ({ name, lon: a.lon, lat: a.lat }))
}

function PointLabel({ text, side, strong }: { text: string; side: 'left' | 'right'; strong?: boolean }) {
  return (
    <span
      className={`pointer-events-none absolute top-1/2 -translate-y-1/2 whitespace-nowrap rounded-[var(--r-control)] border border-[var(--ink-border)] bg-[var(--ink-bg)]/90 px-1.5 py-px text-14 ${
        side === 'left' ? 'right-full mr-2' : 'left-full ml-2'
      } ${strong ? 'font-semibold text-[var(--ink-text)]' : 'text-[var(--ink-text)]'}`}
    >
      {text}
    </span>
  )
}

function SetupMap({
  bounds,
  dam,
  reach,
  settlements,
  site,
}: {
  bounds: Bbox
  dam: DamFull | null
  reach: GeoJsonFeatureCollection | null
  settlements: Settlement[]
  site: { name: string; lat: number; lon: number } | null
}) {
  const ref = useRef<MapRef | null>(null)
  const states = useGeo('india_states.geojson')
  const key = bounds.join(',')

  const avoid = useMemo(
    () => [
      ...settlements.map((s) => ({ lon: s.lon, lat: s.lat })),
      ...(dam ? [{ lon: dam.lon, lat: dam.lat }] : []),
      ...(site ? [{ lon: site.lon, lat: site.lat }] : []),
    ],
    [settlements, dam, site],
  )
  const labels = useMemo(() => stateLabels(states.data, bounds, avoid), [states.data, key, avoid]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="setup-map flex h-full w-full flex-col">
      <style>{MAP_CSS}</style>
      <div className="relative min-h-0 flex-1">
      <BaseMap
        ref={ref}
        bounds={bounds}
        sources={DATA_SOURCES}
        layers={DATA_LAYERS}
        padding={PAD}
        preserveDrawingBuffer
        dragRotate={false}
        onLoad={(e) => {
          if (import.meta.env.DEV) Object.assign(window, { __setupMap: e.target })
        }}
      >

        {reach && (
          <Source id="setup-reach" type="geojson" data={reach}>
            <Layer
              id="setup-reach-line"
              type="line"
              layout={{ 'line-join': 'round', 'line-cap': 'round' }}
              paint={{ 'line-color': RIVER, 'line-width': 2.5 }}
            />
          </Source>
        )}

        {labels.map((l) => (
          <Marker key={l.name} longitude={l.lon} latitude={l.lat} anchor="center" style={{ pointerEvents: 'none' }}>
            <span className="whitespace-nowrap text-12 font-medium uppercase tracking-[0.18em] text-[var(--ink-text-dim)]">
              {l.name}
            </span>
          </Marker>
        ))}

        {settlements.map((s) => (
          <Marker key={s.id} longitude={s.lon} latitude={s.lat} anchor="center" style={{ zIndex: 2 }}>
            <div className="relative h-2.5 w-2.5 rounded-full border border-[var(--ink-bg)] bg-[var(--ink-text)]" data-testid="settlement-marker">
              <PointLabel text={s.name} side="right" />
            </div>
          </Marker>
        ))}

        {dam && (
          <Marker longitude={dam.lon} latitude={dam.lat} anchor="center" style={{ zIndex: 3 }}>
            <div className="relative h-4 w-4" data-testid="dam-marker" title={`${dam.name} ${dam.lat.toFixed(4)} N ${dam.lon.toFixed(4)} E`}>
              <svg viewBox="0 0 16 16" className="block h-4 w-4">
                <path d="M8 1 L15 8 L8 15 L1 8 Z" fill={DAM} stroke={C.text} strokeWidth={1.25} />
              </svg>
              <PointLabel text={dam.name} side="left" strong />
            </div>
          </Marker>
        )}

        {site && (
          <Marker longitude={site.lon} latitude={site.lat} anchor="center" style={{ zIndex: 3 }}>
            <div className="relative h-4 w-4" data-testid="site-marker">
              <svg viewBox="0 0 16 16" className="block h-4 w-4">
                <path d="M8 1 L15 8 L8 15 L1 8 Z" fill={DAM} stroke={C.text} strokeWidth={1.25} />
              </svg>
              <PointLabel text={site.name} side="right" strong />
            </div>
          </Marker>
        )}
      </BaseMap>

      </div>
      <div className="shrink-0 border-t border-[var(--ink-border)] bg-[var(--ink-surface)] px-3 py-2" data-testid="setup-legend">
        <CollapsibleLegend>
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-14 text-[var(--ink-text)]">
          <li className="flex items-center gap-2">
            <svg viewBox="0 0 16 16" className="h-3.5 w-3.5">
              <path d="M8 1 L15 8 L8 15 L1 8 Z" fill={DAM} stroke={C.text} strokeWidth={1.25} />
            </svg>
            Dam
          </li>
          {settlements.length > 0 && (
            <li className="flex items-center gap-2">
              <span className="mx-0.5 h-2.5 w-2.5 rounded-full border border-[var(--ink-bg)] bg-[var(--ink-text)]" />
              Settlement
            </li>
          )}
          {reach && (
            <li className="flex items-center gap-2">
              <span className="h-[3px] w-3.5 rounded-sm" style={{ background: RIVER }} />
              River
            </li>
          )}
          <li className="flex items-center gap-2">
            <svg viewBox="0 0 14 4" className="h-1 w-3.5">
              <line x1="0" y1="2" x2="14" y2="2" stroke={C.textDim} strokeWidth="1.5" strokeDasharray="4 2" />
            </svg>
            State boundary
          </li>
        </ul>
        </CollapsibleLegend>
      </div>
    </div>
  )
}

export default function Setup() {
  const navigate = useNavigate()
  const setActiveScenarioId = useAppStore((s) => s.setActiveScenarioId)
  const [siteId, setSiteId] = useState<string | null>(null)

  const studyArea = useQuery({ queryKey: ['study-area'], queryFn: api.studyArea })
  const dams = useQuery({ queryKey: ['dams'], queryFn: api.dams })
  const settlements = useQuery({ queryKey: ['settlements'], queryFn: api.settlements })
  const sites = useQuery<SitesPayload>({ queryKey: ['sites'], queryFn: () => api.raw('/sites') })
  const states = useGeo('india_states.geojson')

  const dam = (dams.data?.[0] ?? null) as DamFull | null
  const reach = useQuery({
    queryKey: ['reach', dam?.id],
    queryFn: () => api.reach(dam!.id),
    enabled: dam !== null,
  })
  const reachCollection: GeoJsonFeatureCollection | null = useMemo(() => {
    if (!reach.data?.geometry_geojson) return null
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', geometry: JSON.parse(reach.data.geometry_geojson), properties: { name: reach.data.name } }],
    }
  }, [reach.data])

  const runSite = sites.data?.run_site ?? sites.data?.default ?? null
  const site = sites.data?.sites.find((s) => s.id === (siteId ?? runSite)) ?? null
  const atRunSite = site !== null && site.id === runSite

  const bounds: Bbox | null = useMemo(() => {
    if (!site) return null
    if (atRunSite) return studyArea.data?.bbox_wgs84 ?? null
    const state = states.data?.features.find((f) => f.properties.name === site.state)
    return state ? geomBbox(state.geometry) : null
  }, [site, atRunSite, studyArea.data, states.data])

  const figures = atRunSite
    ? [
        typeof site?.reservoir_volume?.value === 'number'
          ? { label: 'Gross storage', value: (site.reservoir_volume.value / 1e9).toFixed(2), unit: 'km³' }
          : null,
        dam?.height_m ? { label: 'Dam height', value: dam.height_m.toFixed(1), unit: 'm' } : null,
        reach.data?.length_km ? { label: 'Downstream reach', value: reach.data.length_km.toFixed(0), unit: 'km' } : null,
      ].filter((f): f is { label: string; value: string; unit: string } => f !== null)
    : []

  return (
    <div className="flex h-full bg-[var(--ink-bg)]">
      <div className="w-[640px] shrink-0 overflow-y-auto border-r border-[var(--ink-border)] p-4 2xl:w-[800px]">
        <ScenarioBuilder
          dam={dam}
          onSiteChange={setSiteId}
          onSaved={(scenario) => {
            setActiveScenarioId(scenario.id)
            navigate('/simulation')
          }}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        {site && (
          <header className="border-b border-[var(--ink-border)] px-5 py-3" data-testid="site-header">
            <div className="flex items-baseline gap-3">
              <h2 className="text-20 font-semibold text-[var(--ink-text)]">{site.display_name}</h2>
              {site.state && <span className={LABEL}>{site.state}</span>}
            </div>
            {figures.length > 0 && (
              <dl className="mt-2 flex flex-wrap gap-x-10 gap-y-2">
                {figures.map((f) => (
                  <div key={f.label}>
                    <dt className={LABEL}>{f.label}</dt>
                    <dd className="font-mono text-32 leading-tight tabular-nums text-[var(--ink-text)]">
                      {f.value}
                      <span className="ml-1.5 text-14 text-[var(--ink-text-dim)]">{f.unit}</span>
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </header>
        )}
        <div className="relative min-h-0 flex-1">
          {bounds ? (
            <SetupMap
              bounds={bounds}
              dam={atRunSite ? dam : null}
              reach={atRunSite ? reachCollection : null}
              settlements={atRunSite ? settlements.data ?? [] : []}
              site={!atRunSite && site?.center ? { name: site.display_name, ...site.center } : null}
            />
          ) : (
            <div className="skeleton h-full w-full" />
          )}
        </div>
      </div>
    </div>
  )
}
